import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Children } from "../../../../components/miscellianous/children"
import { BSBuildingContext } from "../../../../pages/projects/project/beem-shot/variant/building/components/context/BSBuildingContext"
import { BSMorphologyContext } from "../../../../pages/projects/project/beem-shot/variant/tabs/BSBuildingTabs/BSBuildingMorphologyTab/context/BSMorphologyContext"
import { BSInput } from "../../../dto/beem-shot/BSInput/BSInput"
import { BSInputFoundationUpdateDto } from "../../../dto/beem-shot/BSInput/BSInputFoundationUpdateDto"
import { BSInputLevelAndZoneUpdateDto } from "../../../dto/beem-shot/BSInput/BSInputLevelAndZoneUpdateDto"
import { BSInputParkingUpdateDto } from "../../../dto/beem-shot/BSInput/BSInputParkingUpdateDto"
import { BSInputUpdateDto } from "../../../dto/beem-shot/BSInput/BSInputUpdateDto"
import { BSInputUsageUpdateDto } from "../../../dto/beem-shot/BSInput/BSInputUsageUpdateDto"
import { useBSInput } from "../../../hooks/beem-shot/useBSInput"
import { ProjectContext } from "../../project/ProjectContext"

export const BSInputContext = React.createContext<BSInputStore>({} as BSInputStore)

export function BSInputContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { getBSInput, putBSInput, putBSInputFoundationType, putBSInputParking, putBSInputLevelAndZone, putBSInputUsage } =
    useBSInput()
  const { project } = useContext(ProjectContext)
  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const { refreshBSMorphology } = useContext(BSMorphologyContext)

  const [bsInput, setBSInput] = useState<BSInput | undefined>()

  const refreshBSInput = useCallback(() => {
    if (project?.id && selectedBSBuilding?.id) {
      return getBSInput(selectedBSBuilding.id).then((newBSInput) => {
        setBSInput(newBSInput)
      })
    }
    return Promise.resolve()
  }, [project?.id, getBSInput, selectedBSBuilding?.id])

  useEffect(() => {
    refreshBSInput()
  }, [refreshBSInput])

  const update = useCallback(
    (bsInputUpdateDto: BSInputUpdateDto, noUpdate?: boolean) =>
      // Optimistic update
      putBSInput(bsInputUpdateDto).then((response: BSInput) => {
        if (noUpdate) {
          return
        }
        setBSInput(response)
      }),
    [putBSInput]
  )

  const updateBSInputFoundationType: (bsInputFoundationUpdateDto: BSInputFoundationUpdateDto) => Promise<any> = useCallback(
    (bsInputFoundationUpdate) =>
      putBSInputFoundationType(bsInputFoundationUpdate).then((updatedBSInput) => {
        setBSInput(updatedBSInput)
        return updatedBSInput
      }),
    [putBSInputFoundationType]
  )

  const updateBSInputLevelAndZone: (bsInputLevelAndZoneUpdateDto: BSInputLevelAndZoneUpdateDto) => Promise<BSInput> =
    useCallback(
      (bsInputLevelAndZoneUpdateDto) =>
        putBSInputLevelAndZone(bsInputLevelAndZoneUpdateDto).then((updatedBSInput) => {
          setBSInput(updatedBSInput)
          return updatedBSInput
        }),
      [putBSInputLevelAndZone]
    )

  /** V0 Temporary usage update function for bs building NO bim*/
  const updateBSInputUsage: (bsInputUsageUpdateDto: BSInputUsageUpdateDto) => Promise<BSInput> = useCallback(
    (bsInputUsageUpdateDto) =>
      putBSInputUsage(bsInputUsageUpdateDto)
        .then((updatedBSInput) => {
          setBSInput(updatedBSInput)
          return updatedBSInput
        })
        .then((updatedBSInput) => {
          refreshBSMorphology()
          return updatedBSInput
        }),
    [putBSInputUsage, refreshBSMorphology]
  )

  const updateBSInputParking: (bsInputParkingUpdateDto: BSInputParkingUpdateDto) => Promise<any> = useCallback(
    (bsInputParkingUpdate) =>
      putBSInputParking(bsInputParkingUpdate).then((updatedBSInput) => {
        setBSInput(updatedBSInput)
        return updatedBSInput
      }),
    [putBSInputParking]
  )

  const bsInputStore = useMemo(
    () => ({
      bsInput,
      setBSInput,
      updateBSInput: update,
      refreshBSInput,
      updateBSInputFoundationType,
      updateBSInputParking,
      updateBSInputLevelAndZone,
      updateBSInputUsage,
    }),
    [
      bsInput,
      refreshBSInput,
      update,
      updateBSInputFoundationType,
      updateBSInputParking,
      updateBSInputLevelAndZone,
      updateBSInputUsage,
    ]
  )

  return <BSInputContext.Provider value={bsInputStore}>{children}</BSInputContext.Provider>
}

export interface BSInputStore {
  bsInput: BSInput | undefined
  setBSInput: Dispatch<SetStateAction<BSInput | undefined>>
  updateBSInputUsage: (bsInputUsageUpdateDto: BSInputUsageUpdateDto) => Promise<BSInput>

  updateBSInput(bsInputCreationDto: BSInputUpdateDto, noUpdate?: boolean): Promise<void>

  refreshBSInput(): Promise<void>

  updateBSInputFoundationType(bsInputFoundationUpdateDto: BSInputFoundationUpdateDto): Promise<BSInput>

  updateBSInputParking(bsInputParkingUpdateDto: BSInputParkingUpdateDto): Promise<BSInput>

  updateBSInputLevelAndZone(bsInputLevelAndZoneUpdateDto: BSInputLevelAndZoneUpdateDto): Promise<BSInput>
}
